import * as React from 'react';
import { removeAppSplash } from 'utils/ui/splash';

/** The slice of a react-router data router this hook depends on. */
export type TRouterLike = {
  state: { initialized: boolean };
  subscribe: (listener: (state: { initialized: boolean }) => void) => () => void;
};

/**
 * Dismisses the static index.html splash once the router settles its first navigation —
 * the earliest moment real UI exists beneath it (initial loaders resolved, or their
 * error boundary rendered). The double requestAnimationFrame lets React commit and the
 * browser paint that UI before the splash disappears, so no blank frame shows between
 * the two. On host pages without the splash (web-component deployments) this is a no-op.
 */
export function useDismissSplashWhenRouterSettles(router: TRouterLike): void {
  React.useEffect(() => {
    // Dismissal is a one-shot: `initialized` stays true for the router's lifetime, so
    // without the guard every later state update (each navigation) would re-schedule
    // the paint callback.
    let dismissed = false;
    const dismissAfterPaintOnce = () => {
      if (dismissed) {
        return;
      }
      dismissed = true;
      requestAnimationFrame(() => requestAnimationFrame(removeAppSplash));
    };

    if (router.state.initialized) {
      dismissAfterPaintOnce();
      return;
    }

    const unsubscribe = router.subscribe((state) => {
      if (state.initialized) {
        unsubscribe();
        dismissAfterPaintOnce();
      }
    });
    return unsubscribe;
  }, [router]);
}
